// src/components/admin/ChangePasswordDialog.js

import { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    InputAdornment,
    Alert,
    CircularProgress,
    Divider,
    Chip
} from '@mui/material';
import {
    Close as CloseIcon,
    Visibility,
    VisibilityOff,
    Lock as LockIcon,
    Person as PersonIcon,
    Security as SecurityIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../services/api.js';
import { toast } from 'react-toastify';

const ChangePasswordDialog = ({ open, onClose, user }) => {
    const { t } = useTranslation();
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleClose = () => {
        setNewPassword('');
        setConfirmPassword('');
        setShowPassword(false);
        setShowConfirmPassword(false);
        setError('');
        onClose(false);
    };

    const validatePassword = () => {
        if (newPassword.length < 6) {
            setError(t('validation.passwordMinLength', 'Password must be at least 6 characters long'));
            return false;
        }
        if (newPassword.length > 128) {
            setError(t('validation.passwordMaxLength', 'Password cannot exceed 128 characters'));
            return false;
        }
        if (newPassword !== confirmPassword) {
            setError(t('validation.passwordMismatch', 'Passwords do not match'));
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!validatePassword()) {
            return;
        }

        setLoading(true);
        try {
            await authAPI.adminResetPassword(user._id || user.id, newPassword);
            toast.success(
                t('users.passwordResetSuccess', {
                    username: user.username,
                    defaultValue: `Password for ${user.username} has been reset successfully`
                })
            );
            handleClose();
            onClose(true); // Indicate success
        } catch (err) {
            const errorMessage = err.response?.data?.message ||
                t('users.passwordResetError', 'Failed to reset password');
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const generateRandomPassword = () => {
        const length = 12;
        const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
        let password = '';

        // Ensure at least one of each required character type
        password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)];
        password += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)];
        password += '0123456789'[Math.floor(Math.random() * 10)];
        password += '!@#$%^&*'[Math.floor(Math.random() * 8)];

        // Fill the rest randomly
        for (let i = password.length; i < length; i++) {
            password += charset[Math.floor(Math.random() * charset.length)];
        }

        // Shuffle the password
        password = password.split('').sort(() => Math.random() - 0.5).join('');

        setNewPassword(password);
        setConfirmPassword(password);
        setShowPassword(true);
        setShowConfirmPassword(true);
    };

    if (!user) return null;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                // bgcolor: 'primary.main',
                color: 'white',
                py: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SecurityIcon />
                    <Typography variant="h6">
                        {t('users.resetPassword', 'Reset User Password')}
                    </Typography>
                </Box>
                <IconButton
                    onClick={handleClose}
                    size="small"
                    sx={{ color: 'white' }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <form onSubmit={handleSubmit}>
                <DialogContent sx={{ pt: 3 }}>
                    {/* User Info Section */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 3,
                        p: 2,
                        // bgcolor: 'grey.50',
                        borderRadius: 1,
                        border: '1px solid',
                        // borderColor: 'grey.200'
                    }}>
                        <PersonIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                        <Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                                {user.name || user.username}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                @{user.username}
                            </Typography>
                            {user.email && (
                                <Typography variant="body2" color="textSecondary">
                                    {user.email}
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ ml: 'auto' }}>
                            <Chip
                                label={user.userType || 'User'}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                        </Box>
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    {/* Warning Alert */}
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        {t('users.passwordResetWarning',
                            'This action will immediately change the user\'s password. The user will need to use the new password for their next login.'
                        )}
                    </Alert>

                    {/* Error Alert */}
                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                            {error}
                        </Alert>
                    )}

                    {/* Password Fields */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label={t('users.newPassword', 'New Password')}
                            type={showPassword ? 'text' : 'password'}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            fullWidth
                            required
                            autoComplete="new-password"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockIcon color="action" />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            size="small"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            helperText={t('validation.passwordHelper', 'Minimum 6 characters')}
                        />

                        <TextField
                            label={t('users.confirmPassword', 'Confirm Password')}
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            fullWidth
                            required
                            autoComplete="new-password"
                            error={confirmPassword !== '' && newPassword !== confirmPassword}
                            helperText={
                                confirmPassword !== '' && newPassword !== confirmPassword
                                    ? t('validation.passwordMismatch', 'Passwords do not match')
                                    : ''
                            }
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockIcon color="action" />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            edge="end"
                                            size="small"
                                        >
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />

                        {/* Generate Random Password Button */}
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={generateRandomPassword}
                            sx={{ alignSelf: 'flex-start' }}
                        >
                            {t('users.generatePassword', 'Generate Random Password')}
                        </Button>
                    </Box>
                </DialogContent>

                <Divider />

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={handleClose}
                        disabled={loading}
                        color="inherit"
                    >
                        {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={loading || !newPassword || !confirmPassword}
                        startIcon={loading ? <CircularProgress size={20} /> : <LockIcon />}
                    >
                        {loading
                            ? t('common.saving', 'Saving...')
                            : t('users.resetPassword', 'Reset Password')
                        }
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default ChangePasswordDialog;